import React from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';

/**
 * Shared add-on card (v4.12). ONE source of truth for how an add-on plan is
 * shown — used by both the post-add-to-cart AddonsOfferModal upsell AND the
 * new pre-add-to-cart "Available add-ons" section inside AddToCartModal, so
 * the two surfaces can't drift. An add-on is just a meal_plan flagged
 * is_delivery_addon / is_meal_addon (store-wide, no per-plan linkage).
 */
const AddonCard = ({ addon, primary = '#f97316', onAdd, ctaLabel = 'Add' }) => {
  const firstTier = (addon.pricing?.tiers || [])[0] || {};
  const price = firstTier.price || addon.base_price || null;
  const qty = firstTier.quantity || firstTier.meals || null;
  return (
    <div
      className="flex items-center gap-3 rounded-xl bg-white border border-neutral-200 p-3 hover:border-neutral-400 transition"
      data-testid={`addon-card-${addon.id}`}
    >
      {addon.image_url ? (
        <img src={addon.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-lg flex-shrink-0 grid place-items-center" style={{ background: `${primary}18` }}>
          <Plus className="w-6 h-6" style={{ color: primary }} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h4
          className="text-base leading-tight mb-0.5 truncate"
          style={{ fontFamily: 'Playfair Display, ui-serif, Georgia, serif', fontWeight: 500 }}
        >
          {addon.name}
        </h4>
        <div className="flex items-baseline gap-2 flex-wrap text-xs text-neutral-500">
          {qty !== null && <span>{qty} meals</span>}
          {price !== null && (
            <span className="font-semibold" style={{ color: primary }}>
              ${Number(price).toFixed(2)}
            </span>
          )}
          {addon.is_delivery_addon && (
            <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-neutral-200">Delivery add-on</span>
          )}
          {addon.is_meal_addon && !addon.is_delivery_addon && (
            <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-neutral-200">Meal add-on</span>
          )}
        </div>
      </div>
      <motion.button
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        type="button"
        onClick={() => onAdd?.(addon)}
        data-testid={`addon-card-add-${addon.id}`}
        className="rounded-full px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-medium text-white flex-shrink-0"
        style={{ background: primary }}
      >
        <Plus className="w-3 h-3 inline mr-1" />
        {ctaLabel}
      </motion.button>
    </div>
  );
};

export default AddonCard;
